/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ReportUser/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import styles from "./index.module.css";
import { reportUser } from "../../logic/chat";

const ReportUserModal = ({
  open,
  onClose,
  selectedChat,
  selectedUser,
  setChats,
  setSelectedChat,
  setSelectedUser,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [reportReason, setReportReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dialogRef = useRef(null);

  const handleReportUser = async () => {
    setIsSubmitting(true);
    await reportUser(
      selectedChat,
      selectedUser,
      reportReason,
      navigate,
      setChats,
      setSelectedChat,
      setSelectedUser,
    );
    setIsSubmitting(false);
    onClose();
  };

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setReportReason("");
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [open, onClose]);

  return (
    <>
      {open && <div className={styles.overlay}></div>}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        <div className={styles.content}>
          <label className={styles.title}>
            {t("components.reportUserModal.title")}
          </label>
          <label className={styles.subtitle}>
            {t("components.reportUserModal.instructionText")}
          </label>
          <div className={styles.fullWidthContainer}>
            <label className={styles.modalLabel}>
              {t("components.reportUserModal.reportReason")}
            </label>
            <textarea
              placeholder={t(
                "components.reportUserModal.reportReasonPlaceholder",
              )}
              className={styles.textarea}
              value={reportReason || ""}
              onChange={(e) => {
                const value = e.target.value;
                setReportReason(value);
              }}
              maxLength={300}
            />
          </div>
        </div>

        <div className={styles.buttonsContainer}>
          <button
            onClick={onClose}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            {t("components.reportUserModal.cancelButton")}
          </button>
          <button
            className={styles.createButton}
            onClick={handleReportUser}
            disabled={!reportReason || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span>{t("components.reportUserModal.sending")}</span>
              </>
            ) : (
              <>{t("components.reportUserModal.sendButton")}</>
            )}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default ReportUserModal;
